import React from 'react';

import { ProductsChunk, TopBlock } from '../../types/apiCompoundProduct';
import { ProductListItemType } from '../../types/apiCompoundProductList';
import {
  DonationProduct,
  EntityReferenceBlockWithHand,
  GiftcardProduct,
  ProductReferenceBlock,
} from '../../types/apiTypes';
import { RedeemProductListItemPresentational } from '../../types/common';
import { productsHooks } from '../productsHooks/productsHooks';

export const ITEM_POSITION_SHIFT = 1;

export const callbacks = {
  mapTopProduct(product: TopBlock): RedeemProductListItemPresentational {
    const item: Omit<RedeemProductListItemPresentational, 'id' | 'cardImage' | 'path'> = {
      type: ProductListItemType.TopProduct,
      title: product.title,
      description: product.description,
      backgroundImage: product.backgroundImage,
      showHand: product.showHand,
      showShadow: product.showShadow,
      backgroundColor: null,
      image: null,
      variationsToShow: [],
      fixedValues: false,
    };

    if (product.__typename === 'EntityReferenceBlockWithHand') {
      const { title, cardImage, referencedPath } = product as EntityReferenceBlockWithHand;
      return {
        id: title,
        cardImage,
        path: referencedPath,
        ...item,
      };
    }

    const {
      productImage,
      productReference: { id = '' } = {} } = product as ProductReferenceBlock;
    return {
      id,
      cardImage: productImage,
      path: null,
      ...item,
    };
  },
  mapGiftCardProduct(product: GiftcardProduct): RedeemProductListItemPresentational {
    return {
      typename: 'GiftcardProduct',
      id: product.id,
      type: ProductListItemType.GiftcardProduct,
      title: product.title,
      description: product.description,
      path: product.path,
      cardImage: product.redeemData?.image ?? product.image,
      backgroundImage: null,
      showHand: product.showHand,
      backgroundColor: product.backgroundColor,
      image: product.image,
      variationsToShow: product.variationsToShow,
      redeemData: product.redeemData,
      fixedValues: product.fixedValues,
      productType: product.productType,
      showShadow: product.showShadow,
    };
  },
  mapDonationProduct(product: DonationProduct): RedeemProductListItemPresentational {
    return {
      typename: 'DonationProduct',
      id: product.id,
      type: ProductListItemType.GiftcardProduct,
      title: product.redeemData?.title || '',
      description: product.redeemData?.description || null,
      path: null,
      cardImage: product.image,
      backgroundImage: null,
      showHand: false,
      backgroundColor: product.backgroundColor,
      image: product.image,
      variationsToShow: [],
      fixedValues: false,
      productType: product.productType,
    };
  },
};

export const productListHooks = {
  useRedeemProductsMapping(
    productChunks: ProductsChunk[],
  ): { productsListChunks: RedeemProductListItemPresentational[][] } {
    const {
      mapTopProduct,
      mapGiftCardProduct,
      mapDonationProduct,
    } = callbacks;

    const productsListChunks = React.useMemo(() => {
      const listChunks: RedeemProductListItemPresentational[][] = [];

      productChunks.forEach(({
        topProducts,
        products,
      }) => {
        const list: RedeemProductListItemPresentational[] = [];
        if (topProducts.length > 0) {
          topProducts.forEach((topProduct) => {
            list.push(mapTopProduct(topProduct));
          });
        }

        if (products.length > 0) {
          products.forEach(product => {
            if (product.__typename === 'DonationProduct') {
              list.push(mapDonationProduct(product));
            }

            if (product.__typename === 'GiftcardProduct') {
              list.push(mapGiftCardProduct(product));
            }
          });
        }

        listChunks.push(list);
      });

      return listChunks;
    }, [mapDonationProduct, mapGiftCardProduct, mapTopProduct, productChunks]);

    return {
      productsListChunks,
    };
  },
  useCalculateItemPosition({
    page,
  }: {
    page: number;
  }) {
    const allProductsCountPerPage = productsHooks.useProductsPerPage();
    const sumOfProductsPerPage = React.useMemo(
      () => allProductsCountPerPage.productsPerPage + allProductsCountPerPage.topProductsPerPage,
      [allProductsCountPerPage.productsPerPage, allProductsCountPerPage.topProductsPerPage],
    );

    return React.useCallback(
      (index: number) => {
        return index + ITEM_POSITION_SHIFT + (page - 1) * sumOfProductsPerPage;
      },
      [page, sumOfProductsPerPage],
    );
  },
};
